/**
 * ADR-70: which engine serves which ring of a sale.
 *
 * Pure rules over a sale's ring provisioning, shared by control-api (go-live,
 * the live token, readiness) and the clerk console (which ring it connects
 * to). One copy, so the console can never connect a clerk to an engine the
 * API would not have sent a bidder to.
 */

/** One ring as the operator provisions it. Optionals are explicitly
 *  `| undefined`: these always arrive from parsed JSON. */
export interface RingSpec {
  /** The auction engine's own id for the ring: its RING_ID, and what it
   *  writes into the settlement projection. */
  readonly engineRingId: string;
  /** What the sale calls it. Shown to bidders: "about 10 out in Ring 2". */
  readonly name?: string | undefined;
  readonly position?: number | undefined;
  /** Per-ring endpoint, when rings run on separate engines (cut 5b). */
  readonly endpoint?: string | undefined;
  /** The ring's own approval queue (its stack's output, cut 5b). */
  readonly approvalQueueUrl?: string | undefined;
}

/** The addressing half of a sale's ring provisioning. */
export interface RingAddresses {
  readonly ringEndpoint?: string | undefined;
  readonly approvalQueueUrl?: string | undefined;
  readonly rings?: readonly RingSpec[] | undefined;
}

/** The ring every one-ring sale has always run as: the engine's own default. */
export const DEFAULT_ENGINE_RING_ID = 'ring-1';

/** A ring and where it is, for the surfaces that list them. */
export interface RingAddress {
  readonly engineRingId: string;
  readonly name: string;
  readonly endpoint: string | null;
}

/** What a declared ring is called: its name, else its place. */
export const ringSpecName = (spec: RingSpec, i: number): string => spec.name ?? `Ring ${spec.position ?? i + 1}`;

/**
 * Where one ring's engine is.
 *
 * A declared ring has its own endpoint. The sale-level `ringEndpoint` is the
 * FIRST declared ring's (it is what a one-ring sale always had) and never a
 * later ring's: pointing ring 2's clients at ring 1's engine is exactly the
 * failure nobody would see until sale night. A sale that declared no rings
 * runs one, at the sale-level endpoint, as before.
 */
export function ringEndpointFor(prov: RingAddresses, engineRingId: string): string | null {
  if (!prov.rings?.length) return prov.ringEndpoint ?? null;
  const i = prov.rings.findIndex((r) => r.engineRingId === engineRingId);
  if (i < 0) return null;
  return prov.rings[i]!.endpoint ?? (i === 0 ? prov.ringEndpoint ?? null : null);
}

/** Every declared ring with its endpoint, in declaration order. A sale that
 *  declared none lists its one ring under `fallback`. */
export function ringAddresses(prov: RingAddresses, fallback = DEFAULT_ENGINE_RING_ID): RingAddress[] {
  if (!prov.rings?.length) return [{ engineRingId: fallback, name: 'Ring 1', endpoint: prov.ringEndpoint ?? null }];
  return prov.rings.map((r, i) => ({ engineRingId: r.engineRingId, name: ringSpecName(r, i), endpoint: ringEndpointFor(prov, r.engineRingId) }));
}

/**
 * ADR-70 cut 5c: the ring a screen asked for, by its engine ring id. No id
 * means the first ring, which is what every screen did before a sale could
 * have two. An id the sale does not have is null: the screen says so rather
 * than quietly showing the first ring under the wrong name.
 */
export function pickRing(prov: RingAddresses, wanted: string | null | undefined, fallback = DEFAULT_ENGINE_RING_ID): RingAddress | null {
  const all = ringAddresses(prov, fallback);
  if (!wanted) return all[0] ?? null;
  return all.find((r) => r.engineRingId === wanted) ?? null;
}
